<template>
  <div class="analysis-overview-card h-full">
    <div class="space-y-1">
      <!-- 变更文件数 -->
      <div class="flex items-center justify-between overflow-auto">
        <div class="flex-1 pr-3 min-w-0">
          <p class="mb-0 text-sm text-gray-500">变更文件数</p>
          <div class="number-display overflow-hidden">
            <p v-if="!isError && !isLoading" class="truncate text-2xl font-bold text-gray-800 my-0!">
              {{ displayValue(statistics.totalFiles) }}
            </p>
            <div v-else-if="isLoading" class="loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <icon-vue v-else icon="mdi:close-circle" class="text-2xl text-red-500" font-size="24px" />
          </div>
        </div>
        <div
          class="stat-icon flex-shrink-0 rounded-full p-2 transition-all duration-300"
          :class="isError ? 'bg-red-100' : 'bg-orange-100'"
        >
          <icon-vue
            :class="isError ? 'text-red-500' : 'text-orange-500'"
            icon="mdi:file-document-edit-outline"
            font-size="24px"
          />
        </div>
      </div>

      <!-- 测试用例总数 -->
      <div class="flex items-center justify-between">
        <div class="flex-1 pr-3 min-w-0">
          <p class="mb-0 text-sm text-gray-500">测试用例总数</p>
          <div class="number-display overflow-hidden">
            <p v-if="!isError && !isLoading" class="truncate text-2xl font-bold text-gray-800 my-0!">
              {{ displayValue(statistics.totalTestCases) }}
            </p>
            <div v-else-if="isLoading" class="loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <icon-vue v-else icon="mdi:close-circle" class="text-2xl text-red-500" font-size="24px" />
          </div>
        </div>
        <div
          class="stat-icon flex-shrink-0 rounded-full p-2 transition-all duration-300"
          :class="isError ? 'bg-red-100' : 'bg-blue-100'"
        >
          <icon-vue
            :class="isError ? 'text-red-500' : 'text-blue-600'"
            icon="mdi:checkbox-marked-circle-outline"
            font-size="24px"
          />
        </div>
      </div>

      <!-- 推荐最小集 -->
      <div class="flex items-center justify-between">
        <div class="flex-1 pr-3 min-w-0">
          <p class="mb-0 text-sm text-gray-500">推荐最小集</p>
          <div class="number-display overflow-hidden">
            <p v-if="!isError && !isLoading" class="truncate text-2xl font-bold text-gray-800 my-0!">
              {{ displayValue(statistics.recommendedTests) }}
            </p>
            <div v-else-if="isLoading" class="loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <icon-vue v-else icon="mdi:close-circle" class="text-2xl text-red-500" font-size="24px" />
          </div>
        </div>
        <div
          class="stat-icon flex-shrink-0 rounded-full p-2 transition-all duration-300"
          :class="isError ? 'bg-red-100' : 'bg-green-100'"
        >
          <icon-vue
            :class="isError ? 'text-red-500' : 'text-green-600'"
            icon="mdi:lightning-bolt-outline"
            font-size="24px"
          />
        </div>
      </div>

      <!-- 预估覆盖率 -->
      <div class="flex items-center justify-between">
        <div class="flex-1 pr-3 min-w-0">
          <p class="mb-0 text-sm text-gray-500">预估覆盖率</p>
          <div class="flex items-end overflow-hidden">
            <div class="number-display flex-1 min-w-0">
              <p v-if="!isError && !isLoading" class="truncate text-2xl font-bold text-gray-800 my-0!">
                {{ displayValue(statistics.coverageRate) }}
              </p>
              <div v-else-if="isLoading" class="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <icon-vue v-else icon="mdi:close-circle" class="text-2xl text-red-500" font-size="24px" />
            </div>
          </div>
        </div>
        <div
          class="stat-icon flex-shrink-0 rounded-full p-2 transition-all duration-300"
          :class="isError ? 'bg-red-100' : 'bg-green-100'"
        >
          <icon-vue :class="isError ? 'text-red-500' : 'text-green-600'" icon="mdi:chart-line" font-size="24px" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { AnalysisStatus } from '@/types/precision-new/ai-analysis';
import { Icon as IconVue } from '@iconify/vue';
import { computed } from 'vue';

// Props
interface Props {
  status: AnalysisStatus;
  statistics: {
    totalFiles: string | number;
    totalTestCases: string | number;
    recommendedTests: string | number;
    coverageRate: string;
  };
}

const props = withDefaults(defineProps<Props>(), {
  status: 'idle',
  statistics: () => ({
    totalFiles: '-',
    totalTestCases: '-',
    recommendedTests: '-',
    coverageRate: '-',
  }),
});

// 计算属性
const isError = computed(() => props.status === 'error');
const isLoading = computed(() => props.status === 'loading' || props.status === 'analyzing');

// 显示值处理
const displayValue = (value: string | number) => {
  return value;
};
</script>

<style scoped>
/* 强制压缩间距 */
.analysis-overview-card > div > div {
  margin-bottom: 4px !important;
}

.analysis-overview-card > div > div:last-child {
  margin-bottom: 0 !important;
}

/* 覆盖space-y类 */
.space-y-1 > * + * {
  margin-top: 4px !important;
}

/* 加载点动画 */
.loading-dots {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  position: relative;
}

.loading-dots span {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #6b7280;
  animation: loading-dot 1.4s infinite ease-in-out both;
}

.loading-dots span:nth-child(1) {
  left: 8px;
  animation-delay: -0.32s;
}

.loading-dots span:nth-child(2) {
  left: 14px;
  animation-delay: -0.16s;
}

.loading-dots span:nth-child(3) {
  left: 20px;
  animation-delay: 0s;
}

@keyframes loading-dot {
  0%,
  80%,
  100% {
    transform: translateY(-50%) scale(0);
  }
  40% {
    transform: translateY(-50%) scale(1);
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
